import React from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
} from 'chart.js';
import { BLUE_CREDITONE, BLUE_ONAHAU, GREEN_SILVER_TREE, usdFormatter } from '../configs/constants';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
);

type ChartProps = {
    labels: string[],
    data: number[],
    width: number,
};

const Chart = ({ labels, data, width }: ChartProps) => {
    if (!labels.length || !data.length) {
        return (<></>);
    }

    const chartData = {
        labels: labels,
        datasets: [{
            data: data,
            borderColor: BLUE_CREDITONE,
            borderWidth: 2,
            fill: true,
            pointBackgroundColor: BLUE_CREDITONE,
            pointRadius: 4,
            pointHoverRadius: 6,
            hoverBackgroundColor: GREEN_SILVER_TREE,
            hoverBorderColor: GREEN_SILVER_TREE,
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: width > 769,
        plugins: {
            tooltip: {
                enabled: true,
                backgroundColor: BLUE_ONAHAU,
                titleColor: BLUE_CREDITONE,
                bodyColor: 'rgba(0, 0, 0, 1)',
                bodyFontStyle: 'bold',
                displayColors: false,
                callbacks: {
                    title: function (context) {
                        function getOrdinal(number) {
                            const suffixes = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
                            const pr = new Intl.PluralRules('en-US', { type: 'ordinal' });
                            const rule = pr.select(number);

                            return `${number}${suffixes[rule]}`;
                        }
                        const ordinalLabel = getOrdinal(parseInt(context[0].label));
                        return `${ordinalLabel} Month`;
                    },
                    beforeBody: function () {
                        return 'Interest Earned: '
                    },
                    label: function (context) {
                        let label = context.dataset.label || '';

                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += usdFormatter.format(context.parsed.y);
                        }
                        return label;
                    },

                }

            },
        },
        scales: {
            x: {
                grid: { display: false },
                title: {
                    display: true,
                    text: 'Months',
                    color: BLUE_CREDITONE,
                    font: {
                        size: width <= 769 ? 16 : 20,
                        family: 'Open Sans',
                    }
                }
            },
            y: {
                min: 0,
                title: {
                    display: true,
                    text: 'Interest ($)',
                    color: BLUE_CREDITONE,
                    font: {
                        size: width <= 769 ? 16 : 20,
                        family: 'Open Sans',
                    }
                },
                ticks: {
                    stepSize: 500,
                    padding: width <= 769 ? 8 : 16,
                    maxTicksLimit: 6,
                    count: 6,
                }
            }
        },
    };

    return (
        <div className='p-8 md:p-4 relative md:h-72'>
            <Line
                options={options}
                data={chartData}
            />
            { /* Resizing Bug Fix: https://github.com/reactchartjs/react-chartjs-2/issues/1169 */}
            <span className='invisible'>Interest Earning Calculator Graph</span>
        </div>
    );
}

export default Chart;